import "@/App.css";
import "@/i18n";

import { lazy, Suspense, useEffect, useState } from "react";
import { HostTenantContext, isCustomHost, setHostTenant } from "@/lib/hostTenant";
import { useTranslation } from "react-i18next";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";

import { PATH_LOCALES } from "@/lib/locale";

import { Layout } from "@/components/layout/Layout";
import { MicrositeLayout } from "@/components/layout/MicrositeLayout";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ScrollToTop } from "@/components/ScrollToTop";
import { Toaster } from "@/components/ui/sonner";
import { ChatWidget } from "@/components/ai/ChatWidget";
import { AuthProvider } from "@/context/AuthContext";
import { ModeProvider } from "@/context/ModeContext";
import Catalog from "@/pages/Catalog";
import EventLanding from "@/pages/EventLanding";
import ForgotPassword from "@/pages/ForgotPassword";
import Home from "@/pages/Home";
import HorseProfile from "@/pages/HorseProfile";
import OrganizerWebsitePage from "@/pages/OrganizerWebsitePage";
import OrganizerProfilePage from "@/pages/OrganizerProfilePage";
import HowItWorks from "@/pages/HowItWorks";
import ForOrganisers from "@/pages/ForOrganisers";
import ForBuyers from "@/pages/ForBuyers";
import Features from "@/pages/Features";
import CampaignLanding from "@/pages/CampaignLanding";

import About from "@/pages/About";
import { StripeReturnHandler } from "@/components/payments/StripeReturnHandler";
import SubmitHorsePage from "@/pages/SubmitHorsePage";
import Contact from "@/pages/Contact";
import InvitePage from "@/pages/InvitePage";
import LegalPage from "@/pages/LegalPage";
import LegalEnginePage from "@/pages/LegalEnginePage";
import AuctionTermsPage from "@/pages/AuctionTermsPage";
import LotPage from "@/pages/LotPage";
import LiveRoom from "@/pages/LiveRoom";
import Pricing from "@/pages/Pricing";
import { SupportModeBanner } from "@/components/SupportModeBanner";
import ResourcesPage from "@/pages/ResourcesPage";
import ResourceArticlePage from "@/pages/ResourceArticlePage";
import SignIn from "@/pages/SignIn";
import SignUp from "@/pages/SignUp";
import ResetPassword from "@/pages/ResetPassword";
import NotFound from "@/pages/NotFound";

/* WS10 (CWV): code-splitting — las superficies PRIVADAS (dashboard, admin,
   account, builder, wizard, design-system) se cargan bajo demanda para que las
   páginas públicas indexables descarguen un bundle mucho menor. */
const MicrositeRegister = lazy(() => import("@/pages/MicrositeRegister"));
const CreateWizard = lazy(() => import("@/pages/wizard/CreateWizard"));
const DesignSystem = lazy(() => import("@/pages/DesignSystem"));
const DashboardLayout = lazy(() =>
  import("@/components/layout/DashboardLayout").then((m) => ({ default: m.DashboardLayout }))
);
const AccountBids = lazy(() => import("@/pages/account/AccountBids"));
const AccountFavorites = lazy(() => import("@/pages/account/AccountFavorites"));
const AccountNotifications = lazy(() => import("@/pages/account/AccountNotifications"));
const AccountPayments = lazy(() => import("@/pages/account/AccountPayments"));
const AccountVerificationReturn = lazy(() => import("@/pages/account/AccountVerificationReturn"));
const InvoicePage = lazy(() => import("@/pages/account/InvoicePage"));
const AccountSettings = lazy(() => import("@/pages/account/AccountSettings"));
const AccountSubmissions = lazy(() => import("@/pages/account/AccountSubmissions"));
const AccountWon = lazy(() => import("@/pages/account/AccountWon"));
const DashboardOperations = lazy(() => import("@/pages/dashboard/DashboardOperations"));
const DashboardInfrastructure = lazy(() => import("@/pages/dashboard/DashboardInfrastructure"));
const DashboardPayments = lazy(() => import("@/pages/dashboard/DashboardPayments"));
const DashboardAOSBilling = lazy(() => import("@/pages/dashboard/DashboardAOSBilling"));
const AdminBilling = lazy(() => import("@/pages/admin/AdminBilling"));
const ConnectReturn = lazy(() => import("@/pages/dashboard/ConnectReturn"));
const DashboardAnalytics = lazy(() => import("@/pages/dashboard/DashboardAnalytics"));
const AuctionSetup = lazy(() => import("@/pages/dashboard/AuctionSetup"));
const DashboardBuyers = lazy(() => import("@/pages/dashboard/DashboardBuyers"));
const DashboardEventResults = lazy(() => import("@/pages/dashboard/DashboardEventResults"));
const DashboardEvents = lazy(() => import("@/pages/dashboard/DashboardEvents"));
const DashboardSponsors = lazy(() => import("@/pages/dashboard/DashboardSponsors"));
const DashboardSubmissions = lazy(() => import("@/pages/dashboard/DashboardSubmissions"));
const AuctioneerConsole = lazy(() => import("@/pages/dashboard/AuctioneerConsole"));
const DashboardHorses = lazy(() => import("@/pages/dashboard/DashboardHorses"));
const DashboardLandingBuilder = lazy(() => import("@/pages/dashboard/DashboardLandingBuilder"));
const DashboardWebsite = lazy(() => import("@/pages/dashboard/DashboardWebsite"));
const LandingBuilder = lazy(() => import("@/pages/builder/LandingBuilder"));
const PreviewFrame = lazy(() => import("@/pages/builder/PreviewFrame"));
const DashboardMedia = lazy(() => import("@/pages/dashboard/DashboardMedia"));
const DashboardOverview = lazy(() => import("@/pages/dashboard/DashboardOverview"));
const DashboardSettings = lazy(() => import("@/pages/dashboard/DashboardSettings"));
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const AdminOverview = lazy(() => import("@/pages/admin/AdminOverview"));
const AdminUsers = lazy(() => import("@/pages/admin/AdminUsers"));
const AdminOrganizers = lazy(() => import("@/pages/admin/AdminOrganizers"));
const AdminAuctions = lazy(() => import("@/pages/admin/AdminAuctions"));
const AdminEconomics = lazy(() => import("@/pages/admin/AdminEconomics"));
const AdminErrors = lazy(() => import("@/pages/admin/AdminErrors"));
const AdminActivity = lazy(() => import("@/pages/admin/AdminActivity"));

/* Fallback de Suspense — discreto, accesible, coherente con el design system. */
const RouteFallback = () => (
  <div className="min-h-[50vh] flex items-center justify-center" aria-busy="true" data-testid="route-fallback">
    <div className="h-6 w-6 rounded-full border-2 border-[#0A0A0A]/20 border-t-[#0A0A0A] animate-spin" />
  </div>
);

/* Fase 13 (WS3): el prefijo de idioma en la URL manda sobre la preferencia
   guardada — /es/pricing SIEMPRE se muestra en español. */
function LocaleSync() {
  const location = useLocation();
  const { i18n } = useTranslation();
  useEffect(() => {
    const seg = location.pathname.split("/")[1];
    if (PATH_LOCALES.includes(seg) && (i18n.resolvedLanguage || "").slice(0, 2) !== seg) {
      i18n.changeLanguage(seg);
    }
  }, [location.pathname, i18n]);
  return null;
}

/* Rutas de NIVEL 1 duplicadas por idioma: EN en raíz, /es /de /fr /it. */
const localized = (path, element) =>
  ["", ...PATH_LOCALES.map((l) => `/${l}`)].map((prefix) => (
    <Route key={`${prefix}${path}`} path={`${prefix}${path}`} element={element} />
  ));

function App() {
  /* Fase E1 — dominios personalizados: si la app se sirve bajo un host que no
     es AOS/preview, resolvemos el tenant una sola vez en el bootstrap. En el
     dominio principal (tenant === null) TODO sigue exactamente igual. */
  const [tenant, setTenant] = useState(() => (isCustomHost() ? undefined : null));
  useEffect(() => {
    if (tenant !== undefined) return;
    fetch(`/api/public/domains/resolve?host=${encodeURIComponent(window.location.hostname)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        setHostTenant(data);
        setTenant(data);
      })
      .catch(() => {
        setHostTenant(null);
        setTenant(null);
      });
  }, [tenant]);
  if (tenant === undefined) return <RouteFallback />;

  return (
    <HostTenantContext.Provider value={tenant}>
    <AuthProvider>
      <BrowserRouter>
        <ModeProvider>
        <ScrollToTop />
        <LocaleSync />
        <SupportModeBanner />
        <StripeReturnHandler />
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Fase E1 — bajo dominio personalizado conectado, la web del
              organizador y sus subastas viven en la RAÍZ de ese dominio.
              Este bloque solo existe cuando hay tenant (nunca en AOS). */}
          {tenant && (
            <Route element={<MicrositeLayout />}>
              <Route path="/" element={<OrganizerWebsitePage />} />
              <Route path="/about" element={<OrganizerProfilePage />} />
              <Route path="/:eventSlug" element={<EventLanding />} />
              <Route path="/:eventSlug/register" element={<MicrositeRegister />} />
              <Route path="/:eventSlug/live" element={<LiveRoom />} />
              <Route path="/:eventSlug/submit" element={<SubmitHorsePage />} />
              <Route path="/:eventSlug/lots/:lotId" element={<LotPage />} />
            </Route>
          )}
          <Route element={<Layout />}>
            {localized("/", <Home />)}
            {localized("/how-it-works", <HowItWorks />)}
            {localized("/for-organisers", <ForOrganisers />)}
            {localized("/for-buyers", <ForBuyers />)}
            {localized("/features", <Features />)}
            {/* WS16: landings de campaña por audiencia (noindex, preservan UTM) */}
            <Route path="/lp/:audience" element={<CampaignLanding />} />
            {localized("/auctions", <Catalog />)}
            {localized("/pricing", <Pricing />)}
            {localized("/about", <About />)}
            {localized("/contact", <Contact />)}
            {localized("/resources", <ResourcesPage />)}
            {localized("/resources/:slug", <ResourceArticlePage />)}
            {localized("/legal/terms", <LegalEnginePage docKey="platform_terms" />)}
            {localized("/legal/compliance", <LegalEnginePage docKey="compliance_declaration" />)}
            <Route path="/auction-terms/:eventId" element={<AuctionTermsPage />} />
            {localized("/legal/privacy", <LegalPage doc="privacy" />)}
            {localized("/legal/cookies", <LegalPage doc="cookies" />)}
            <Route path="/signin" element={<SignIn />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/design-system" element={<DesignSystem />} />
            <Route
              path="/create"
              element={
                <ProtectedRoute>
                  <CreateWizard />
                </ProtectedRoute>
              }
            />
            {/* Buyer account — lives inside the public shell */}
            <Route path="/account" element={<ProtectedRoute><AccountFavorites /></ProtectedRoute>} />
            <Route path="/account/bids" element={<ProtectedRoute><AccountBids /></ProtectedRoute>} />
            <Route path="/account/won" element={<ProtectedRoute><AccountWon /></ProtectedRoute>} />
            <Route path="/account/notifications" element={<ProtectedRoute><AccountNotifications /></ProtectedRoute>} />
            <Route path="/account/submissions" element={<ProtectedRoute><AccountSubmissions /></ProtectedRoute>} />
            <Route path="/account/horses" element={<ProtectedRoute><AccountFavorites only="horses" /></ProtectedRoute>} />
            <Route path="/account/payments" element={<ProtectedRoute><AccountPayments /></ProtectedRoute>} />
            <Route path="/account/verification/return" element={<ProtectedRoute><AccountVerificationReturn /></ProtectedRoute>} />
            <Route path="/account/invoices" element={<Navigate to="/account/payments" replace />} />
            <Route path="/account/invoices/:invoiceId" element={<ProtectedRoute><InvoicePage /></ProtectedRoute>} />
            <Route path="/invite/:token" element={<InvitePage />} />
            <Route path="/account/settings" element={<ProtectedRoute><AccountSettings /></ProtectedRoute>} />
          </Route>
          {/* Landing Builder — full-screen workspace outside both shells */}
          <Route
            path="/dashboard/events/:eventId/builder"
            element={
              <ProtectedRoute roles={["organizer", "admin"]}>
                <LandingBuilder />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/events/:eventId/preview-frame"
            element={
              <ProtectedRoute roles={["organizer", "admin"]}>
                <PreviewFrame />
              </ProtectedRoute>
            }
          />
          {/* Auctioneer Console — full-screen live cockpit outside both shells */}
          <Route
            path="/dashboard/events/:eventId/console"
            element={
              <ProtectedRoute roles={["organizer", "admin"]}>
                <AuctioneerConsole />
              </ProtectedRoute>
            }
          />
          {/* Fase 10 — Platform Administration (acceso discreto vía logo del footer) */}
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminOverview />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="organizers" element={<AdminOrganizers />} />
            <Route path="auctions" element={<AdminAuctions />} />
            <Route path="economics" element={<AdminEconomics />} />
            <Route path="billing" element={<AdminBilling />} />
            <Route path="infrastructure" element={<DashboardInfrastructure />} />
            <Route path="errors" element={<AdminErrors />} />
            <Route path="activity" element={<AdminActivity />} />
          </Route>
          {/* Organizer OS — separate shell with its own sidebar navigation */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute roles={["organizer", "admin"]}>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<DashboardOverview />} />
            <Route path="infrastructure" element={<Navigate to="/admin/infrastructure" replace />} />
            <Route path="events" element={<DashboardEvents />} />
            <Route path="events/:eventId/setup" element={<AuctionSetup />} />
            <Route path="horses" element={<DashboardHorses />} />
            <Route path="media" element={<DashboardMedia />} />
            <Route path="buyers" element={<DashboardBuyers />} />
            <Route path="events/:eventId/results" element={<DashboardEventResults />} />
            <Route path="invitations" element={<Navigate to="/dashboard/buyers" replace />} />
            <Route path="sponsors" element={<DashboardSponsors />} />
            <Route path="submissions" element={<DashboardSubmissions />} />
            <Route path="website" element={<DashboardWebsite />} />
            <Route path="landing-builder" element={<DashboardLandingBuilder />} />
            <Route path="analytics" element={<DashboardAnalytics />} />
            {/* /dashboard/team removed — QA freeze: no coming-soon surfaces */}
            <Route path="team" element={<Navigate to="/dashboard/settings" replace />} />
            <Route path="payments" element={<DashboardPayments />} />
            <Route path="billing" element={<DashboardAOSBilling />} />
            <Route path="payments/connect/return" element={<ConnectReturn mode="return" />} />
            <Route path="payments/connect/refresh" element={<ConnectReturn mode="refresh" />} />
            <Route path="operations" element={<DashboardOperations />} />
            <Route path="settings" element={<DashboardSettings />} />
          </Route>
          <Route element={<MicrositeLayout />}>
            <Route path="/e/:slug" element={<EventLanding />} />
            <Route path="/e/:slug/register" element={<MicrositeRegister />} />
            <Route path="/e/:slug/live" element={<LiveRoom />} />
            <Route path="/e/:slug/submit" element={<SubmitHorsePage />} />
            <Route path="/e/:slug/lots/:lotId" element={<LotPage />} />
            <Route path="/horses/:horseId" element={<HorseProfile />} />
            {/* Branded organizer paths — the ORGANIZER WEBSITE (must stay LAST) */}
            <Route path="/:orgSlug" element={<OrganizerWebsitePage />} />
            <Route path="/:orgSlug/about" element={<OrganizerProfilePage />} />
            <Route path="/:orgSlug/:eventSlug" element={<EventLanding />} />
            <Route path="/:orgSlug/:eventSlug/register" element={<MicrositeRegister />} />
            <Route path="/:orgSlug/:eventSlug/live" element={<LiveRoom />} />
            <Route path="/:orgSlug/:eventSlug/submit" element={<SubmitHorsePage />} />
            <Route path="/:orgSlug/:eventSlug/lots/:lotId" element={<LotPage />} />
          </Route>
          {/* 404 real (rutas profundas sin match) — noindex + navegación útil */}
          <Route element={<Layout />}>
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
        </Suspense>
        <ChatWidget />
        </ModeProvider>
      </BrowserRouter>
      <Toaster position="bottom-right" />
    </AuthProvider>
    </HostTenantContext.Provider>
  );
}

export default App;
